import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const AnimatedNumber = ({ value, suffix = "", duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            animateNumber();
          }
        });
      },
      { threshold: 0.5 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, [hasAnimated]);

  const animateNumber = () => {
    const startTime = Date.now();
    const startValue = 0;
    const endValue = parseInt(value);

    const animate = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.floor(startValue + (endValue - startValue) * easeOut);

      setCount(currentValue);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    requestAnimationFrame(animate);
  };

  return (
    <span ref={elementRef}>
      {count}{suffix}
    </span>
  );
};

const StatsSection = () => {
  const stats = [
    {
      id: 1,
      number: 500,
      suffix: "+",
      label: "Clientes Satisfeitos",
      description: "Famílias que confiaram em nossos móveis artesanais",
      icon: "Users",
      color: "primary"
    },
    {
      id: 2,
      number: 1000,
      suffix: "+",
      label: "Móveis Fabricados",
      description: "Peças únicas criadas com dedicação e excelência",
      icon: "Package",
      color: "accent"
    },
    {
      id: 3,
      number: 15,
      suffix: "+",
      label: "Anos de Experiência",
      description: "Tradição artesanal passada de geração em geração",
      icon: "Award",
      color: "primary"
    }
  ];

  const aboutText = {
    title: "Sobre a PH Móveis",
    description: "Somos uma empresa familiar especializada em móveis artesanais únicos, combinando tradição brasileira com design contemporâneo. Cada peça é criada com madeiras nobres certificadas e técnicas artesanais que garantem durabilidade e beleza incomparáveis."
  };

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* About Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 bg-muted rounded-full px-4 py-2 mb-6">
            <Icon name="Sparkles" size={16} color="var(--color-primary)" />
            <span className="text-primary font-medium text-sm">Nossa História</span>
          </div>
          
          <h2 className="font-playfair text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            {aboutText.title}
          </h2>
          
          <p className="text-text-secondary text-lg leading-relaxed mb-8 max-w-3xl mx-auto">
            {aboutText.description}
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <div className="flex items-center space-x-2 text-text-secondary">
              <Icon name="Check" size={16} color="var(--color-primary)" />
              <span className="text-sm">Madeiras Certificadas</span>
            </div>
            <div className="flex items-center space-x-2 text-text-secondary">
              <Icon name="Check" size={16} color="var(--color-primary)" />
              <span className="text-sm">Garantia Vitalícia</span>
            </div>
            <div className="flex items-center space-x-2 text-text-secondary">
              <Icon name="Check" size={16} color="var(--color-primary)" />
              <span className="text-sm">Design Personalizado</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {stats.map((stat) => (
            <div
              key={stat.id}
              className="bg-card border border-border rounded-2xl p-6 hover:shadow-premium hover:border-primary/30 transition-all duration-300 text-center"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Icon name={stat.icon} size={24} color="var(--color-primary)" />
              </div>
              
              <div className="font-playfair text-4xl md:text-5xl font-bold text-primary mb-2">
                <AnimatedNumber value={stat.number} suffix={stat.suffix} duration={2000} />
              </div>
              
              <h3 className="font-playfair text-lg font-bold text-foreground mb-2">
                {stat.label}
              </h3>
              
              <p className="text-text-secondary text-sm leading-relaxed">
                {stat.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
